import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import { User, TtStakeFiles } from "@/CodegenTypes";
import { first, sumBy, startCase, sortBy } from "lodash";
import { customMoment } from "@/main";

import Clusterize from "clusterize.js";

import pdfMake from "pdfmake";
import pdfMakeFont from "pdfmake/build/vfs_fonts";
import { formatCount } from "@/helpers/formatCount";
import TTNumberStakeModal from "@/components/modules/bet-details/TTNumberStakeModal.vue";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import { useQuery } from "@vue/apollo-composable";
import { useTranslation } from "@/store/translation";
import InputText from "primevue/inputtext/InputText";

type SortMode = "number-asc" | "number-desc" | "unit-asc" | "unit-desc";

type StakeFileRow = { numbersText: string; unit: number };

type StakeFileTypes = keyof Pick<
  TtStakeFiles,
  "masterStake" | "downlineEatStake" | "companyFinalStake" | "outsetStake"
>;


export default /*#__PURE__*/_defineComponent({
  __name: 'StakeFile',
  setup(__props) {

const route = useRoute();
const { $t } = useTranslation();

const vfs = (pdfMakeFont as any)?.pdfMake?.vfs ?? (pdfMakeFont as any)?.vfs;

const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) : query;
});

const { result, loading, refetch } = useQuery<{
  ttStakeFile: {
    masterStake: StakeFileRow[];
    downlineEatStake: StakeFileRow[];
    companyFinalStake: StakeFileRow[];
    outsetStake: StakeFileRow[];
  };
}>(queries.ttStakeFile, {
  releaseDate: selectedStartReleaseDate.value,
});

const {
  result: meResult,
  refetch: refetchMe,
  loading: loadingMe,
} = useQuery<{ me: User }>(queries.me, {}, { fetchPolicy: "cache-first" });

const me = computed(() => {
  return meResult.value?.me;
});

watch(selectedStartReleaseDate, () => {
  console.log(
    "selectedStartReleaseDate changed",
    selectedStartReleaseDate.value
  );
  if (selectedStartReleaseDate.value) {
    refetch({ releaseDate: selectedStartReleaseDate.value });
  }
});


const ttStakeFile = computed(() => {
  return result.value?.ttStakeFile;
});

const loadedAt = ref(new Date());

const numberFilter = ref("");

const showNumberStakeModal = ref(false);
const selectedNumber = ref("");

const sortMode = ref<SortMode>("number-asc");

let clusterize = null as Clusterize | null;
const scrollArea = ref<HTMLDivElement | null>(null);
const contentArea = ref<HTMLTableSectionElement | null>(null);

const generatingPdf = ref(false);

const selectedBetTypeId = ref<StakeFileTypes>("companyFinalStake");

const betTypes: { name: string; id: StakeFileTypes }[] = [
  { name: "Master Bets", id: "masterStake" },
  { name: "Downline Intake", id: "downlineEatStake" },
  { name: "Company Out", id: "outsetStake" },
  { name: "Company Final", id: "companyFinalStake" },
];

const selectedBetType = computed(() => {
  return betTypes.find((bt) => bt.id === selectedBetTypeId.value);
});

const selectedStake = computed(() => {
  const selectedStake = ttStakeFile.value?.[selectedBetTypeId.value];

  let stake = selectedStake?.filter((s) => s.unit);

  if (numberFilter.value) {
    stake = selectedStake?.filter((s) =>
      s.numbersText.includes(numberFilter.value)
    );
  }
  return sortBy(stake, (s) => {
    return s.numbersText.length;
  });
});

const sortedRows = computed(() => {
  let rowsDataSorted = selectedStake.value;
  if (sortMode.value === "number-asc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => {
      // compare length first
      const diffInLength = a.numbersText.length - b.numbersText.length;
      if (diffInLength) {
        return diffInLength;
      }

      return a.numbersText.localeCompare(b.numbersText);
    });
  } else if (sortMode.value === "number-desc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => {
      // compare length first
      const diffInLength = b.numbersText.length - a.numbersText.length;
      if (diffInLength) {
        return diffInLength;
      }

      return b.numbersText.localeCompare(a.numbersText);
    });
  } else if (sortMode.value === "unit-asc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => a.unit - b.unit);
  } else if (sortMode.value === "unit-desc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => b.unit - a.unit);
  }
  return rowsDataSorted;
});

const renderedRows = computed(() => {
  const rows = sortedRows.value?.map((row) => {
    return `
      <tr>
        <td class="text-center">
          <a
            id="${row.numbersText}"
            class="font-bold text-primary-600 cursor-pointer number-row"
            >${row.numbersText}</a
          >
        </td>
            <td>${formatCount(row.unit)}</td>
      </tr>
      `;
  });
  return rows;
});

function handleSelectNumber(number: string) {
  selectedNumber.value = number;
  showNumberStakeModal.value = true;
}

watch(renderedRows, doClusterize, { immediate: true });
function doClusterize() {
  if (clusterize) {
    clusterize?.update(renderedRows.value);
  } else {
    if (!scrollArea.value || !contentArea.value) {
      return;
    }
    clusterize = new Clusterize({
      rows: renderedRows.value,
      show_no_data_row: false,
      scrollElem: scrollArea.value,
      contentElem: contentArea.value,
      rows_in_block: 20,
      callbacks: {
        clusterChanged: () => {
          const numberRows = document.getElementsByClassName("number-row");
          for (const row of numberRows) {
            row.addEventListener("click", () => {
              handleSelectNumber(row.id);
            });
          }
        },
      },
    });
  }
}

const stakeTotal = computed(() => {
  if (!ttStakeFile.value) {
    return null;
  }

  return {
    masterStake: {
      unit: sumBy(ttStakeFile.value.masterStake, (d) => d.unit),
      count: Object.values(ttStakeFile.value.masterStake).filter((v) => v.unit)
        .length,
    },
    downlineEatStake: {
      unit: sumBy(ttStakeFile.value.downlineEatStake, (d) => d.unit),
      count: Object.values(ttStakeFile.value.downlineEatStake).filter(
        (v) => v.unit
      ).length,
    },
    outsetStake: {
      unit: sumBy(ttStakeFile.value.outsetStake, (d) => d.unit),
      count: Object.values(ttStakeFile.value.outsetStake).filter((v) => v.unit)
        .length,
    },
    companyFinalStake: {
      unit: sumBy(ttStakeFile.value.companyFinalStake, (d) => d.unit),
      count: Object.values(ttStakeFile.value.companyFinalStake).filter(
        (v) => v.unit
      ).length,
    },
  } as {
    [id: string]: {
      unit: number;
      count: number;
    };
  };
});

function exportCsv() {
  const selected = selectedStake.value;
  if (!selected) {
    return;
  }
  let exportText = ``;

  exportText += `,,,This file is for analyze use only. As CSV/Excel files can be edited it cannot be used as proof. \r\n`;
  exportText += `,,,We will not take responsibility for data in CSV/Excel downloads. \r\n`;
  exportText += `,,,To download stake proof use Download Pdf. \r\n`;
  exportText += `Number,Unit\r\n`;

  selected.forEach((row) => {
    exportText += `${row.numbersText},${row.unit.toFixed(2)}`;
    exportText += `\r\n`;
  });
  const data = new Blob([exportText], { type: "text/plain" });

  // If we are replacing a previously generated file we need to
  // manually revoke the object URL to avoid memory leaks.
  // if (textFile !== null) {
  //   window.URL.revokeObjectURL(textFile);
  // }

  const textFileUrl = window.URL.createObjectURL(data);

  const link = document.createElement("a");
  link.href = textFileUrl;
  const betTypeName = betTypes.find((bt) => bt.id === selectedBetTypeId.value);
  const fileName = betTypeName?.name || "stake";
  link.setAttribute("download", fileName + ".csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function exportPdf() {
  if (!selectedStake.value || !selectedStartReleaseDate.value || !me.value) {
    return;
  }
  const fileName = startCase(selectedBetTypeId.value);
  const releaseDateStr = formatDate(selectedStartReleaseDate.value);
  // const reportType = this.selectedRelease.locked
  //   ? "Final"
  //   : "Bet In Progress";
  const docDef = {
    pageMargins: [40, 60, 40, 60],
    watermark: {
      text: `${releaseDateStr}`,
      opacity: 0.1,
    },
    pageSize: {
      width: 595.28,
      height: 100000,
    },
    header: {
      columns: [
        { text: `${fileName}` },
        { text: `Draw: ${releaseDateStr}`, alignment: "right" },
      ],
      margin: [40, 20],
    },
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        `Generated @ ${formatDatetime(
          loadedAt.value
        )} by ${me.value?.userId.toUpperCase()}`,
        { text: `Pg ${currentPage} of ${pageCount}`, alignment: "right" },
      ],
      margin: [20, 20],
    }),
    content: [
      {
        table: {
          layout: "lightHorizontalLines", // optional
          // headers are automatically repeated if the table spans over multiple pages
          // you can declare how many rows should be treated as headers
          headerRows: 1,
          widths: ["*", "*", "*", "*"],
          body: [
            [
              { text: "Number", alignment: "center" },
              { text: "Unit", alignment: "right" },
            ],
            ...selectedStake.value.map((stake) => [
              { text: stake.numbersText, alignment: "center" },
              { text: formatCount(stake.unit), alignment: "right" },
            ]),
            [
              {
                text: `Total (${
                  stakeTotal.value?.[selectedBetTypeId.value].count || 0
                })`,
                alignment: "center",
              },
              {
                text: formatCount(
                  stakeTotal.value?.[selectedBetTypeId.value].unit || 0
                ),
                alignment: "right",
              },
            ],
          ],
        },
      },
    ],
  };
  (pdfMake as any).createPdf(docDef, null, null, vfs).download(fileName + ".pdf");
}

function formatDate(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD/MM/YYYY");
}
function formatDatetime(date: Date) {
  return customMoment(date).format("ddd, DD/MM/YYYY hh:mm:ss A");
}

return { __sfc: true,route, $t, vfs, selectedStartReleaseDate, result, loading, refetch, meResult, refetchMe, loadingMe, me, ttStakeFile, loadedAt, numberFilter, showNumberStakeModal, selectedNumber, sortMode, clusterize, scrollArea, contentArea, generatingPdf, selectedBetTypeId, betTypes, selectedBetType, selectedStake, sortedRows, renderedRows, handleSelectNumber, doClusterize, stakeTotal, exportCsv, exportPdf, formatDate, formatDatetime, formatCount, TTNumberStakeModal, InputText }
}

})